import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";

import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  clavePermiso,
  seleccionablesDe,
  agruparIds,
  aplicarMasivoA
} from "./seleccion";
import {
  getRole,
  listPermissions,
  setRolePermissions,
  createRole
} from "../../services/access.api";

/**
 * Orden fijo de columnas: es el de la matriz que definió el cliente.
 *
 * Solo la CLAVE, que es el dato real del catálogo. La etiqueta se traduce al
 * pintar: estaban escritas en español a mano, así que la pantalla se quedaba en
 * español aunque el panel estuviera en inglés o portugués.
 */
const ACTIONS = ["ver", "crear", "editar", "eliminar", "aprobar", "procesar", "administrar"];

/**
 * Traduce una clave del catálogo con reserva.
 *
 * WHY la reserva: los módulos y acciones los define la base (migración 282). Si
 * mañana una migración añade uno y nadie añade su traducción, i18n devolvería la
 * clave cruda —"roleMatrix.modules.facturas"— en mitad de la tabla. Con la
 * reserva se ve al menos el nombre del módulo, legible.
 */
const traducir = (grupo, clave) =>
  i18n.t(`roleMatrix.${grupo}.${clave}`, {
    defaultValue: String(clave).replace(/-/g, " ")
  });

/** Textos del modal. Van aparte porque varios llevan interpolación. */
const t = (clave, vars) => i18n.t(`roleMatrix.modal.${clave}`, vars);

/**
 * Casilla de selección masiva: toda la matriz, una columna o una fila.
 *
 * Solo opera sobre `ids`, que son los permisos que el usuario PODRÍA marcar uno
 * a uno —existen en el catálogo y no los bloquea la regla de no-escalada—.
 *
 * WHY esa restricción: `sp_role_set_permissions` valida el conjunto ENTERO al
 * guardar, no el cambio. Si una selección masiva marcara un permiso que el
 * editor no posee, el guardado fallaría con «No puede otorgar N permiso(s) que
 * usted no tiene» y nadie sabría cuál de las cien casillas lo provocó. La regla
 * es simple: lo masivo hace exactamente lo que harían los clics uno a uno.
 */
const SelectorMasivo = ({ ids, selected, disabled, onChange, etiquetas }) => {
  const marcados = ids.reduce((n, id) => (selected.has(id) ? n + 1 : n), 0);
  const todos = ids.length > 0 && marcados === ids.length;

  return (
    <Tooltip title={todos ? etiquetas.quitar : etiquetas.poner}>
      {/* El <span> es necesario: un control deshabilitado no emite eventos y el
          Tooltip se quedaría mudo justo cuando hace falta explicar por qué. */}
      <span>
        <Checkbox
          color="primary"
          size="small"
          checked={todos}
          indeterminate={marcados > 0 && !todos}
          onChange={() => onChange(ids, !todos)}
          disabled={disabled || ids.length === 0}
        />
      </span>
    </Tooltip>
  );
};

const useStyles = makeStyles((theme) => ({
  header: { paddingBottom: 0 },
  subtitle: { color: theme.palette.text.secondary, fontSize: 13 },
  identityRow: { display: "flex", gap: 16, marginBottom: 16, marginTop: 8 },
  matrixWrapper: {
    maxHeight: 420,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4
  },
  moduleCell: { fontWeight: 500 },
  headCell: { fontWeight: 600, whiteSpace: "nowrap" },
  /** Casilla junto al texto, sin robarle ancho a la columna del módulo. */
  cellConSelector: { display: "flex", alignItems: "center", gap: 2 },
  /** En las columnas la casilla va DEBAJO del rótulo: apilarlas mantiene el
      ancho que ya tenía la tabla, que es lo que preocupaba del diseño. */
  columnHead: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    lineHeight: 1.1
  },
  /** Celda sin permiso posible: el guion evita una casilla que no significa nada. */
  emptyCell: { color: theme.palette.text.disabled, textAlign: "center" },
  systemHint: {
    background: theme.palette.action.hover,
    padding: "8px 12px",
    borderRadius: 4,
    marginBottom: 12,
    fontSize: 13
  }
}));

/**
 * Editor de la matriz de un rol — la pantalla que pidió el cliente.
 *
 * Dos reglas de la UI vienen del backend, no se inventan aquí:
 *   · `isSystem` -> el rol es COMPARTIDO por todas las empresas. Ya no se
 *     bloquea: al guardar, el backend crea una copia propia de la empresa y
 *     aplica el cambio ahi (copia al escribir, migraciones 330-331). Aqui solo
 *     se avisa de que va a ocurrir.
 *   · `ungrantable` -> permisos que el editor no puede delegar por no tenerlos
 *     él mismo. Se muestran deshabilitados en vez de dejar marcarlos y fallar al
 *     guardar (regla de no-escalada, H12).
 */
const RoleModal = ({ open, onClose, roleId, onSaved }) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [ungrantable, setUngrantable] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [draft, setDraft] = useState({ code: "", name: "", scope: "equipo" });

  const isNew = !roleId;

  useEffect(() => {
    if (!open) return;

    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [{ data: perms }, roleData] = await Promise.all([
          listPermissions(),
          roleId ? getRole(roleId) : Promise.resolve({ data: null })
        ]);

        if (!active) return;

        setCatalog(perms?.catalog ?? []);
        setUngrantable(perms?.ungrantable ?? []);

        if (roleData?.data) {
          setRole(roleData.data);
          setSelected(new Set(roleData.data.permissionIds ?? []));
        } else {
          setRole(null);
          setSelected(new Set());
          setDraft({ code: "", name: "", scope: "equipo" });
        }
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, roleId]);

  /** Catálogo indexado por módulo: cada fila de la matriz. */
  const matrix = useMemo(() => {
    const byModule = new Map();
    catalog.forEach((p) => {
      if (!byModule.has(p.module)) byModule.set(p.module, {});
      byModule.get(p.module)[p.action] = p;
    });
    return [...byModule.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [catalog]);

  const ungrantableSet = useMemo(() => new Set(ungrantable), [ungrantable]);
  // Editable salvo SUPERADMIN, que es intocable para todos —incluido el propio
  // superadministrador— porque nadie podria devolverle los permisos si se los
  // quitara (guarda 0 del SP).
  const readOnly = role?.code === "SUPERADMIN";

  // Un rol de sistema SI se edita: lo que cambia es que el guardado creara una
  // version propia de la empresa en vez de tocar la compartida.
  const creaCopia = Boolean(role?.isSystem) && role?.code !== "SUPERADMIN";

  /**
   * Los permisos que la selección masiva puede tocar: existen y no están
   * bloqueados. Todo lo masivo se calcula a partir de aquí.
   */
  const seleccionables = useMemo(
    () => seleccionablesDe(catalog, ungrantableSet),
    [catalog, ungrantableSet]
  );

  const idsTodos = useMemo(() => seleccionables.map((p) => p.id), [seleccionables]);

  const idsPorAccion = useMemo(
    () => agruparIds(seleccionables, "action"),
    [seleccionables]
  );
  const idsPorModulo = useMemo(
    () => agruparIds(seleccionables, "module"),
    [seleccionables]
  );

  const aplicarMasivo = useCallback((ids, marcar) => {
    setSelected((prev) => aplicarMasivoA(prev, ids, marcar));
  }, []);

  const etiquetasMasivas = (grupo) => ({
    poner: traducir("bulk", grupo),
    quitar: traducir("bulk", `${grupo}Clear`)
  });

  const toggle = useCallback((permissionId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) next.delete(permissionId);
      else next.add(permissionId);
      return next;
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      let targetId = roleId;

      if (isNew) {
        const { data } = await createRole(draft);
        targetId = data?.id;
      }

      const { data } = await setRolePermissions(targetId, [...selected]);
      toast.success(data?.message || t("saved"));

      if (data?.affectedUsers > 0) {
        // El cambio de permisos invalida la sesión de los afectados: avisarlo
        // evita el "a mí no me aparece" cuando tengan que volver a entrar.
        toast.info(t("sessionsNotice", { total: data.affectedUsers }));
      }

      onSaved?.();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle className={classes.header}>
        {isNew ? t("new") : t("edit", { name: role?.name ?? "" })}
        <div className={classes.subtitle}>
          {t("subtitle")}
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            {readOnly && (
              <div className={classes.systemHint}>
                {t("superadminNotice")}
              </div>
            )}

            {creaCopia && (
              <div className={classes.systemHint}>
                {t("copyNotice")}
              </div>
            )}

            <div className={classes.identityRow}>
              <TextField
                label={t("code")}
                value={isNew ? draft.code : role?.code ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))
                }
                disabled={!isNew}
                variant="outlined"
                size="small"
                style={{ width: 200 }}
              />
              <TextField
                label={t("name")}
                value={isNew ? draft.name : role?.name ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                disabled={!isNew && readOnly}
                variant="outlined"
                size="small"
                fullWidth
              />
            </div>

            <div className={classes.matrixWrapper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {/* La esquina manda sobre toda la matriz, como en una hoja
                        de cálculo: es donde el usuario ya la busca. */}
                    <TableCell className={classes.headCell}>
                      <div className={classes.cellConSelector}>
                        <SelectorMasivo
                          ids={idsTodos}
                          selected={selected}
                          disabled={readOnly}
                          onChange={aplicarMasivo}
                          etiquetas={etiquetasMasivas("all")}
                        />
                        <span>{traducir("columns", "module")}</span>
                      </div>
                    </TableCell>
                    {ACTIONS.map((a) => (
                      <TableCell key={a} align="center" className={classes.headCell}>
                        <div className={classes.columnHead}>
                          <span>{traducir("actions", a)}</span>
                          <SelectorMasivo
                            ids={idsPorAccion.get(a) ?? []}
                            selected={selected}
                            disabled={readOnly}
                            onChange={aplicarMasivo}
                            etiquetas={etiquetasMasivas("column")}
                          />
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matrix.map(([module, actions]) => (
                    <TableRow key={module} hover>
                      <TableCell className={classes.moduleCell}>
                        <div className={classes.cellConSelector}>
                          <SelectorMasivo
                            ids={idsPorModulo.get(module) ?? []}
                            selected={selected}
                            disabled={readOnly}
                            onChange={aplicarMasivo}
                            etiquetas={etiquetasMasivas("row")}
                          />
                          <span>{traducir("modules", module)}</span>
                        </div>
                      </TableCell>
                      {ACTIONS.map((a) => {
                        const permission = actions[a];

                        // Sin permiso en el catálogo, la celda no existe: un
                        // guion es más honesto que una casilla desmarcada.
                        if (!permission) {
                          return (
                            <TableCell key={a} className={classes.emptyCell}>
                              —
                            </TableCell>
                          );
                        }

                        // Misma clave que usa la selección masiva: si las dos
                        // formas divergieran, una casilla podría verse
                        // habilitada y aun así romper el guardado.
                        const blocked = ungrantableSet.has(
                          clavePermiso({ module, action: a })
                        );
                        const disabled = readOnly || blocked;

                        const box = (
                          <Checkbox
                            color="primary"
                            size="small"
                            checked={selected.has(permission.id)}
                            onChange={() => toggle(permission.id)}
                            disabled={disabled}
                          />
                        );

                        return (
                          <TableCell key={a} align="center" padding="none">
                            {blocked ? (
                              <Tooltip title={t("frozenTooltip")}>
                                <span>{box}</span>
                              </Tooltip>
                            ) : (
                              box
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Typography variant="caption" color="textSecondary">
              {t("selectedCount", { total: selected.size })}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || loading || readOnly || (isNew && !draft.code)}
        >
          {saving ? <CircularProgress size={20} /> : t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleModal;
